import * as Yup from "yup";

import {
  CreateTargetBadges,
  CreateTargetBadgesVariables,
} from "_graphql/mutation/__generated__/CreateTargetBadges";
import { Modal, OfficeButton } from "components";

import CreateTargetBadgeForm from "components/forms/add-target-badge";
import { createTargetBadges } from "../../_graphql/visal/mutations/target";
import { predefinedBadges } from "utils/badges";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

const giftSchema = Yup.object().shape({
  gift_name: Yup.string().required("Gift name is required"),
  gift_type: Yup.string().required("Gift type is required"),
  gift_image: Yup.string().url("Must be a valid URL"),
  gift_description: Yup.string().required("Gift description is required"),
});

const badgeSchema = Yup.object().shape({
  name: Yup.string().required("Badge name is required"),
  min_points: Yup.number()
    .required("Minimum points are required")
    .min(0, "Minimum Points must be at least 0"),
  max_points: Yup.number()
    .required("Max points are required")
    .min(1, "Max points must be at least 1"),
  description: Yup.string().required("Description is required"),
  cash_equivalent: Yup.number()
    .required("Cash equivalent is required")
    .min(0, "Cash equivalent must be at least 0"),
  minimum_cash_equivalent: Yup.number()
    .required("Minimum cash equivalent is required")
    .min(0, "Cash equivalent must be at least 0"),
  gift_basket: Yup.array().of(giftSchema).max(5, "Maximum 5 gifts allowed"),
});

const validationSchema = Yup.object().shape({
  target_id: Yup.string().required("Target ID is required"),
  badges: Yup.array().of(badgeSchema).min(1, "At least one badge is required"),
});

export default function CreateTargetBadgeContainer({
  open,
  setOpen,
  targetId,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  targetId: string;
  refetch?: () => void;
}) {
  const [execute, { loading }] = useMutation<
    CreateTargetBadges,
    CreateTargetBadgesVariables
  >(createTargetBadges, {
    refetchQueries: ["FetchTarget"],
  });

  const form = useFormik<CreateTargetBadgesVariables>({
    initialValues: {
      target_id: targetId,
      badges: predefinedBadges,
    },
    // validationSchema,
    onSubmit: async (variables, helpers) => {
      execute({ variables })
        .then(({ data }) => {
          if (data?.createTargetBadges) {
            toast.success("Target badges configured successfully");
            setOpen(false);
            helpers.resetForm();
            refetch && refetch();
          } else {
            toast.error("An error occurred while configuring target badges");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Configure Target Badges"
      description="Set cash equivalent values in Ghana Cedis (GHS ₵) and add gift items"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          disabled={loading}
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Please wait..." : "Configure Badges"}
        </OfficeButton>
      )}
    >
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
        role="alert"
      >
        <div className="font-bold">Badge Configuration</div>
        <p>
          Standard badge levels with predefined point ranges have been set up
          for you. Please focus on configuring cash equivalents and gift items
          for each badge.
        </p>
        <p className="mt-2 italic text-sm">
          Badge names, descriptions, and point ranges have been pre-configured
          and cannot be modified.
        </p>
      </div>
      <CreateTargetBadgeForm form={form} />
    </Modal>
  );
}
